import { useEffect, useMemo, useRef, useState } from "react";
import { OrderStatusEnum } from "src/enums/order.enum";

const STATUS_OPTIONS: { value: OrderStatusEnum; label: string }[] = [
  { value: OrderStatusEnum.OPEN, label: "Open" },
  { value: OrderStatusEnum.READY, label: "Ready" },
  { value: OrderStatusEnum.CLOSED, label: "Closed" },
  { value: OrderStatusEnum.VOIDED, label: "Voided" },
];

export interface OrderStatusMenuSelectProps {
  value: OrderStatusEnum | string | undefined;
  onChange: (status: OrderStatusEnum) => void;
  disabled?: boolean;
  /** Optional wrapper classes (width is content-sized by default) */
  className?: string;
  /** Only these statuses appear (e.g. batch update: OPEN + CLOSED). Default: all. */
  allowedStatuses?: OrderStatusEnum[];
  /**
   * Full-width trigger + panel (Employees → Timecards employee filter style:
   * py-2, text-sm, border #E5E7EB, light menu with #fef3ef selected row).
   */
  fullWidth?: boolean;
  /**
   * h-8 trigger fills the wrapper width (set `className` e.g. `w-44 shrink-0`) so the control
   * lines up with fixed-width siblings such as the tip field in order detail drawers.
   */
  stretchToWrapper?: boolean;
  /** Accessible name for the trigger when no visible label wraps the control */
  ariaLabel?: string;
}

/**
 * Same interaction/visual pattern as the Timecards employee / time-range dropdowns:
 * bordered trigger, chevron, light panel with selected row highlight (#fef3ef).
 */
export function OrderStatusMenuSelect({
  value,
  onChange,
  disabled = false,
  className = "",
  allowedStatuses,
  fullWidth = false,
  stretchToWrapper = false,
  ariaLabel = "Order status",
}: OrderStatusMenuSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    if (allowedStatuses?.length) {
      const allow = new Set(allowedStatuses);
      return STATUS_OPTIONS.filter((o) => allow.has(o.value));
    }
    return STATUS_OPTIONS;
  }, [allowedStatuses]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const label =
    selected?.label ??
    (value ? String(value).replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase()) : "Select status");

  const triggerCompact =
    "inline-flex w-max max-w-full h-8 items-center justify-end gap-x-1 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-2 text-[13px] font-medium text-[#111827] transition-colors hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:cursor-not-allowed disabled:opacity-[0.5] disabled:bg-[#f3f4f6] disabled:text-gray-500 disabled:border-[#e5e7eb] disabled:hover:bg-[#f3f4f6]";
  const triggerStretchCompact =
    "flex w-full min-w-0 h-8 items-center justify-between gap-x-1 rounded-md border border-[#E5E7EB] bg-white px-2 text-[13px] font-medium text-[#111827] transition-colors hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:cursor-not-allowed disabled:opacity-[0.5] disabled:bg-[#f3f4f6] disabled:text-gray-500 disabled:border-[#e5e7eb] disabled:hover:bg-[#f3f4f6]";
  const triggerField =
    "inline-flex w-full min-w-0 items-center justify-between gap-x-2 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-medium text-[#111827] transition-colors hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] disabled:cursor-not-allowed disabled:opacity-[0.5] disabled:bg-[#f3f4f6] disabled:text-gray-500 disabled:border-[#e5e7eb] disabled:hover:bg-[#f3f4f6]";
  const stretchedField = stretchToWrapper && !fullWidth;
  const chevronClass = fullWidth ? "h-5 w-5 shrink-0 text-[#9ca3af]" : "h-4 w-4 shrink-0 text-[#9ca3af]";
  const panelClass =
    fullWidth || stretchedField
      ? "absolute left-0 right-0 z-[100] mt-2 w-full origin-top-left rounded-md border border-[#E5E7EB] bg-white shadow-lg"
      : "absolute left-0 z-[100] mt-2 min-w-full w-max origin-top-left rounded-md border border-[#E5E7EB] bg-white shadow-lg";
  const triggerClass = fullWidth ? triggerField : stretchedField ? triggerStretchCompact : triggerCompact;
  const wrapperLayout = fullWidth ? "block w-full" : stretchedField ? "block min-w-0" : "inline-block max-w-full";

  return (
    <div className={`relative text-left ${wrapperLayout} ${className}`.trim()} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        className={triggerClass}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {stretchedField ? <span className="min-w-0 truncate text-left">{label}</span> : label}
        <svg className={chevronClass} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div className={panelClass} role="listbox">
          <div className="py-1">
            {options.map((opt) => {
              const isSelected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    isSelected ? "bg-[#fef3ef] font-medium text-[#111827]" : "text-[#374151] hover:bg-[#f9fafb]"
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
